import React, { useState, useMemo, useEffect } from 'react';
import { resolveImage } from './CatalogHelpers';

/**
 * Carrusel de imágenes con flechitas laterales cuando el producto tiene más
 * de una imagen (image_url + extra_images). Si sólo hay una, se comporta
 * como un `<img>` normal.
 *
 * Props:
 * - mainImage: string | null (image_url)
 * - extraImages: string[]  (extra_images)
 * - alt: string
 * - className, imgClassName: opcionales
 * - fallbackEmoji: string mostrado si no hay imágenes
 */
export function ProductImageCarousel({
  mainImage,
  extraImages,
  alt,
  className = 'aspect-square bg-slate-100 relative overflow-hidden',
  imgClassName = 'w-full h-full object-cover',
  fallbackEmoji = '📦',
  priority = false, // si true, la imagen carga eager y con alta prioridad
  children,
}) {
  const images = useMemo(() => {
    const list = [];
    if (mainImage) list.push(mainImage);
    if (Array.isArray(extraImages)) {
      for (const im of extraImages) {
        if (im && !list.includes(im)) list.push(im);
      }
    }
    return list;
  }, [mainImage, extraImages]);

  const [idx, setIdx] = useState(0);

  // Si cambia la lista (por ejemplo tras filtrado), volver a la 1ª
  useEffect(() => { setIdx(0); }, [images.length]);

  const total = images.length;
  const hasMultiple = total > 1;

  const go = (delta) => (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    setIdx((i) => (i + delta + total) % total);
  };

  const resolved = total > 0 ? resolveImage(images[idx]) : null;

  return (
    <div className={className} data-testid="product-image-carousel">
      {resolved ? (
        <img
          src={resolved}
          alt={alt}
          className={imgClassName}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchpriority={priority ? 'high' : 'low'}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-4xl text-slate-300">{fallbackEmoji}</div>
      )}

      {hasMultiple && (
        <>
          <button
            type="button"
            onClick={go(-1)}
            aria-label="Imagen anterior"
            className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/45 hover:bg-black/70 active:scale-95 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-md transition"
            data-testid="carousel-prev"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button
            type="button"
            onClick={go(1)}
            aria-label="Imagen siguiente"
            className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/45 hover:bg-black/70 active:scale-95 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-md transition"
            data-testid="carousel-next"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          {/* Contador tipo "2 / 5" */}
          <div className="absolute bottom-1.5 right-1.5 bg-black/55 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
            {idx + 1} / {total}
          </div>
          {/* Puntitos indicadores */}
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1 pointer-events-none">
            {images.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full ${i === idx ? 'bg-white' : 'bg-white/50'}`}
              />
            ))}
          </div>
        </>
      )}

      {/* Slot para badges/botones overlay (top-left, top-right, etc.) */}
      {children}
    </div>
  );
}

/**
 * Ícono "i" clickable que abre un modal con la información del producto.
 * Se renderiza si hay `info`, `sku`, `purchaseCost` o stock por sucursal — SKU,
 * costo y desglose de stock (M:N A:N) se muestran camuflados al final del modal,
 * en texto muy discreto.
 */
export function ProductInfoButton({ productName, info, sku, purchaseCost, stockM, stockA, commission, wholesale }) {
  const [open, setOpen] = useState(false);
  const hasInfo = !!(info && info.trim());
  const hasStockBreakdown = (stockM !== undefined && stockM !== null) || (stockA !== undefined && stockA !== null);
  const hasCommission = commission !== undefined && commission !== null && commission !== '' && Number(commission) > 0;
  const hasWholesale = wholesale !== undefined && wholesale !== null && wholesale !== '' && Number(wholesale) > 0;
  const hasFooter = !!sku || (purchaseCost !== undefined && purchaseCost !== null && purchaseCost !== '') || hasStockBreakdown || hasCommission || hasWholesale;
  if (!hasInfo && !hasFooter) return null;

  const fmtCost = () => {
    const n = Number(purchaseCost);
    if (!isFinite(n) || n <= 0) return null;
    // Camuflaje: se ve como un valor CSS negativo en px
    const str = Number.isInteger(n) ? String(n) : String(n).replace(/\.?0+$/, '');
    return `-${str}px`;
  };
  const costStr = fmtCost();

  const fmtBreak = () => {
    if (!hasStockBreakdown) return null;
    const m = stockM ?? 0;
    const a = stockA ?? 0;
    return `M:${m} A:${a}`;
  };
  const breakStr = fmtBreak();

  const fmtCommission = () => {
    if (!hasCommission) return null;
    const n = Number(commission);
    const str = Number.isInteger(n) ? String(n) : String(n).replace(/\.?0+$/, '');
    return `c:${str}`;
  };
  const commissionStr = fmtCommission();

  const fmtWholesale = () => {
    if (!hasWholesale) return null;
    const n = Number(wholesale);
    const str = Number.isInteger(n) ? String(n) : String(n).replace(/\.?0+$/, '');
    return `ma:${str}`;
  };
  const wholesaleStr = fmtWholesale();

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        aria-label="Ver información del producto"
        title="Ver información del producto"
        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 hover:bg-sky-500 hover:text-white text-sky-600 backdrop-blur shadow-md border border-slate-200 flex items-center justify-center font-serif font-black text-lg transition z-10"
        data-testid="product-info-btn"
      >
        <span style={{ fontFamily: 'Georgia, serif', lineHeight: 1 }}>i</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="product-info-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-4 border-b sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-sky-100 text-sky-700 font-serif font-black text-lg" style={{ fontFamily: 'Georgia, serif' }}>i</span>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Información</div>
                  <div className="font-bold text-slate-900 leading-tight break-words">{productName}</div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Cerrar"
                className="text-slate-400 hover:text-slate-700 text-2xl leading-none ml-2 -mt-1"
                data-testid="product-info-close"
              >×</button>
            </div>
            {hasInfo && (
              <div className="p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="product-info-body">
                {info}
              </div>
            )}
            {hasFooter && (
              <div
                className={`px-4 pb-4 ${hasInfo ? 'pt-1' : 'pt-4'} text-[11px] text-slate-400 flex flex-wrap gap-x-3 gap-y-0.5 leading-snug select-text`}
                data-testid="product-info-footer"
              >
                {sku && <span data-testid="product-info-sku">SKU: {sku}</span>}
                {costStr && <span data-testid="product-info-cost">{costStr}</span>}
                {commissionStr && <span data-testid="product-info-commission">{commissionStr}</span>}
                {wholesaleStr && <span data-testid="product-info-wholesale">{wholesaleStr}</span>}
                {breakStr && <span data-testid="product-info-stock-breakdown">{breakStr}</span>}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
